import { useSyncExternalStore } from 'react';

import {
  type CookieCategory,
  type CookieChoice,
  type CookieConsent,
  allowsCategory,
  consentIsCurrent,
  cookieConsent,
  parseConsent,
  storageCategory,
} from '@escala/domain/legal';

/**
 * A escolha de cookies deste navegador e o que ela permite guardar. A escolha em si é
 * essencial (`escala:consent`, 12 meses); o resto do `localStorage` passa por aqui:
 * `preferenceStorage` só grava o que a categoria da chave (`storageCategory`, o catálogo
 * de `@escala/domain/legal`) permite, e desligar uma categoria apaga na hora o que ela
 * tinha guardado. Antes da escolha, só os essenciais.
 *
 * Fica fora do React, como o estado do PWA: o tema, o login e a instalação leem na hora,
 * e as telas acompanham com `useCookieConsent()`.
 */
export const CONSENT_KEY = 'escala:consent';

let current: CookieConsent | null = null;
let loaded = false;
const listeners = new Set<() => void>();

function read(): CookieConsent | null {
  try {
    const consent = parseConsent(window.localStorage.getItem(CONSENT_KEY));

    return consentIsCurrent(consent, new Date()) ? consent : null;
  } catch {
    return null;
  }
}

/** A escolha em vigor (`null` = ainda não escolheu, ou venceu). */
export function getConsent(): CookieConsent | null {
  if (!loaded) {
    current = read();
    loaded = true;
  }

  return current;
}

export function allows(category: CookieCategory): boolean {
  return allowsCategory(getConsent(), category);
}

/** Apaga do navegador o que a escolha não permite mais (só as chaves do catálogo). */
function purge(consent: CookieConsent | null) {
  try {
    const keys = Array.from({ length: window.localStorage.length }, (_, i) => window.localStorage.key(i));

    for (const key of keys) {
      const category = key ? storageCategory(key) : null;

      if (key && category && !allowsCategory(consent, category)) window.localStorage.removeItem(key);
    }
  } catch {
    // Armazenamento bloqueado: não há o que apagar.
  }
}

/** Grava a escolha ("Aceitar todos", "Só essenciais" ou a personalizada). */
export function saveConsent(choice: CookieChoice): CookieConsent {
  const consent = cookieConsent(choice, new Date());

  current = consent;
  loaded = true;

  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    // Sem armazenamento: a escolha vale até recarregar.
  }

  purge(consent);
  listeners.forEach((listener) => listener());

  return consent;
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  const onStorage = (event: StorageEvent) => {
    // Outra aba escolheu: vale aqui também.
    if (event.key !== CONSENT_KEY) return;

    current = read();
    listener();
  };

  window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useCookieConsent(): CookieConsent | null {
  return useSyncExternalStore(subscribe, getConsent, () => null);
}

/**
 * O `localStorage` das preferências: grava só com a categoria permitida (sem escolha, só
 * o essencial). Ler e apagar valem sempre.
 */
export const preferenceStorage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    const category = storageCategory(key) ?? 'functional';

    if (!allows(category)) return;

    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Sem armazenamento (janela anônima, cota): vale até recarregar.
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Sem armazenamento: nada a apagar.
    }
  },
};

/** Testes: esquece a escolha lida (o `localStorage` é limpo por quem chama). */
export function resetConsent() {
  current = null;
  loaded = false;
  listeners.forEach((listener) => listener());
}
